import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Menu, X } from 'lucide-react';
import Logo from 'assets/images/logo.png';
import Button from 'shared/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { getLocalizedPath } from 'utils/languageHelpers';
import { URLS } from 'configs';
import './Header.scss';

const Header = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMenuContentClick = event => {
    if (event.target === event.currentTarget) {
      toggleMobileMenu();
    }
  };

  const navigationLinks = [
    {
      to: getLocalizedPath('/'),
      title: t('nav_link_home', 'Home'),
      subtitle: t('nav_link_home_subtitle', 'Return to the Likiee landing experience.'),
    },
    {
      to: getLocalizedPath('/look-builder'),
      title: t('nav_link_look_builder', 'Look Builder'),
      subtitle: t('nav_link_look_builder_subtitle', 'Build complete outfits from any fashion photo instantly.'),
    },
    {
      to: getLocalizedPath('/virtual-try-on'),
      title: t('nav_link_virtual_tryon', 'Virtual Try-On'),
      subtitle: t('nav_link_virtual_tryon_subtitle', 'Preview outfits with instant AI try-on experiences.'),
    },
    {
      to: getLocalizedPath('/price-tracking'),
      title: t('nav_link_price_tracking', 'Price Tracking'),
      subtitle: t('nav_link_price_tracking_subtitle', 'Follow product price drops and never miss a deal.'),
    },
    // {
    //   to: getLocalizedPath('/ai-comparison'),
    //   title: t('nav_link_ai_compare', 'AI Compare'),
    //   subtitle: t('nav_link_ai_compare_subtitle', 'Compare brands side-by-side with smart insights.'),
    // },
    {
      to: getLocalizedPath('/shared-wishlists'),
      title: t('nav_link_wishlists', 'Wishlists'),
      subtitle: t('nav_link_wishlists_subtitle', 'Curate and share collections with friends and stylists.'),
    },
  ];

  return (
    <>
      <header className='landing-header'>
        <nav className='landing-header__nav' aria-label='Main navigation'>
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className='landing-header__logo-link' aria-label='Likiee Home'>
            <img src={Logo} alt='Likiee' className='landing-header__logo-img' />
          </Link>

          {/* Desktop Navigation */}
          <div className='landing-header__desktop-nav'>
            <Link to={getLocalizedPath('/')} className='landing-header__nav-link'>
              {t('nav_link_home', 'Home')}
            </Link>
            <Link to={getLocalizedPath('/look-builder')} className='landing-header__nav-link'>
              {t('nav_link_look_builder', 'Look Builder')}
            </Link>
            <Link to={getLocalizedPath('/virtual-try-on')} className='landing-header__nav-link'>
              {t('nav_link_virtual_tryon', 'Virtual Try-On')}
            </Link>
            <Link to={getLocalizedPath('/price-tracking')} className='landing-header__nav-link'>
              {t('nav_link_price_tracking', 'Price Tracking')}
            </Link>
            {/* <Link to={getLocalizedPath('/ai-comparison')} className='landing-header__nav-link'>
              {t('nav_link_ai_compare', 'AI Compare')}
            </Link> */}
            <Link to={getLocalizedPath('/shared-wishlists')} className='landing-header__nav-link'>
              {t('nav_link_wishlists', 'Wishlists')}
            </Link>
          </div>

          {/* CTA Buttons - Desktop */}
          <div className='landing-header__cta-group'>
            <a href={URLS.CONTACT} className='landing-header__cta-link'>
              <Button variant='default'>{t('contact_us', 'Contact us')}</Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className='landing-header__mobile-menu-btn'
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls='landing-mobile-menu'
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`landing-mobile-menu-overlay ${mobileMenuOpen ? 'is-open' : ''}`}
        onClick={toggleMobileMenu}
        role='button'
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleMobileMenu();
          }
        }}
        aria-label='Close menu'
      />

      {/* Mobile Menu */}
      <div
        id='landing-mobile-menu'
        className={`landing-mobile-menu ${mobileMenuOpen ? 'is-open' : ''}`}
        role='dialog'
        aria-modal='true'
        aria-label='Likiee mobile navigation'
      >
        <div className='landing-mobile-menu__content' onClick={handleMenuContentClick} role='presentation'>
          <div className='landing-mobile-menu__topbar'>
            <button
              type='button'
              className='landing-mobile-menu__close'
              onClick={toggleMobileMenu}
              aria-label={t('mobile_menu_close', 'Close navigation menu')}
            >
              <X aria-hidden='true' />
            </button>
          </div>

          <nav className='landing-mobile-menu__nav' aria-label='Primary mobile links'>
            {navigationLinks.map(link => {
              const isActive = link.to === '/' ? location.pathname === '/' : location.pathname.startsWith(link.to);

              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`landing-mobile-menu__nav-link ${isActive ? 'is-active' : ''}`}
                  onClick={toggleMobileMenu}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div className='landing-mobile-menu__nav-link-text'>
                    <span className='landing-mobile-menu__nav-link-title'>{link.title}</span>
                    <span className='landing-mobile-menu__nav-link-subtitle'>{link.subtitle}</span>
                  </div>
                  <ChevronRight className='landing-mobile-menu__nav-link-icon' aria-hidden='true' />
                </Link>
              );
            })}
          </nav>

          <div className='landing-mobile-menu__cta-group landing-mobile-menu__cta-group--footer'>
            <a href={URLS.CONTACT} onClick={toggleMobileMenu} className='landing-mobile-menu__cta-link'>
              <Button variant='default' style={{ width: '100%' }}>
                {t('contact_us', 'Contact us')}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
